import clsx from 'clsx'

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styles from './index.module.css'

type Props = {
  children: React.ReactNode
  onDragEnd?: (...args: Array<any>) => any
  onDragStart?: (...args: Array<any>) => any
  resize?: number
}

const getPositionWithLimits = ({ parentNode, childrenNode, position }) => {
  if (!parentNode || !childrenNode) return position
  const parentOffset = parentNode.getBoundingClientRect()
  const childrenOffset = childrenNode.getBoundingClientRect()
  const childrenHeight = childrenOffset.height
  const childrenWidth = childrenOffset.width
  const parentWith = parentOffset.width
  const parentHeight = parentOffset.height
  const calcPosition = {
    top: Math.max(Math.min(position.top, parentHeight - childrenHeight), 0),
    left: Math.max(Math.min(position.left, parentWith - childrenWidth), 0),
  }
  return calcPosition
}

const defaultPos = {
  top: 0,
  left: 0,
}

/**
 * A draggable container that allows moving elements within bounds with mouse interaction
 * @param children - The content to be made draggable
 * @param onDragEnd - Optional callback called when dragging ends, receives final position
 * @param onDragStart - Optional callback called when dragging starts
 * @param resize - Optional resize parameter that triggers position recalculation
 * @returns A div container with mouse event handlers for dragging functionality
 */
const DragAndDropMove = ({
  children,
  onDragEnd,
  onDragStart,
  resize,
}: Props): React.ReactElement<'div'> => {
  const [dragStarted, setDragStarted] = useState(false)
  const [dragStart, setDragStart] = useState(false)
  const [mouseOffset, setMouseOffset] = useState(defaultPos)
  const [position, setPosition] = useState(defaultPos)
  const itemRef = useRef<HTMLDivElement>(null)
  const itemContainerRef = useRef<HTMLDivElement>(null)

  const dragEndHandler = () => {
    setDragStart(false)

    if (onDragEnd && itemRef.current) {
      onDragEnd({
        left: itemRef.current.offsetLeft,
        top: itemRef.current.offsetTop,
      })
    }
  }

  useEffect(() => {
    document.addEventListener('mouseup', dragEndHandler)
    return () => {
      document.removeEventListener('mouseup', dragEndHandler)
    }
  }, [])
  useLayoutEffect(() => {
    if (itemContainerRef.current && itemRef.current) {
      const newPosition = getPositionWithLimits({
        parentNode: itemContainerRef.current,
        childrenNode: itemRef.current,
        position,
      })
      setPosition(newPosition)
    }
  }, [resize])
  return (
    <div
      ref={itemContainerRef}
      className={styles.dragAndDropMove}
      onMouseUp={dragEndHandler}
      onMouseMove={event => {
        if (dragStart && itemContainerRef.current && itemRef.current) {
          const containerOffset =
            itemContainerRef.current.getBoundingClientRect()
          const top = event.pageY - containerOffset.top - mouseOffset.top
          const left = event.pageX - containerOffset.left - mouseOffset.left
          const newPosition = getPositionWithLimits({
            parentNode: itemContainerRef.current,
            childrenNode: itemRef.current,
            position: {
              top,
              left,
            },
          })
          setPosition(newPosition)
          setDragStarted(true)
        }
      }}
    >
      <div
        ref={itemRef}
        className={clsx(styles.dragAndDropMoveItem, {
          [styles.dragAndDropMoveItemAbsolute]: dragStarted,
        })}
        style={dragStarted ? { ...position } : undefined}
        onMouseDown={event => {
          const containerOffset = event.currentTarget.getBoundingClientRect()
          setDragStart(true)
          setMouseOffset({
            top: event.pageY - containerOffset.top,
            left: event.pageX - containerOffset.left,
          })

          if (onDragStart) {
            onDragStart()
          }
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default DragAndDropMove
